/**
 * resolveInsertSourceBodyEnd — the INSERT…SELECT analogue of
 * `resolveDrilledSubqueryBodyEnd` (`../select/resolveDrilledSubqueryBodyEnd.ts`).
 *
 * Every ordinary drilled subquery body is paren-wrapped, so its CURRENT end
 * offset can be re-derived by scanning forward from the (stable) body start at
 * paren-depth 1 until the matching close paren. An INSERT's source SELECT is
 * NOT paren-wrapped — `INSERT INTO t (a) SELECT … FROM …` has no closing paren
 * to scan for, so `resolveDrilledSubqueryBodyEnd` would always return `null`
 * for it.
 *
 * Instead we re-run the token-aware INSERT walker (`extractInsertOutline`)
 * over the CURRENT buffer and the enclosing statement's (stable) range, and
 * read back its freshly-derived `selectSourceEnd`. That boundary is already
 * bounded by `findTailKeyword` (stops at a depth-0 `ON CONFLICT` /
 * `ON DUPLICATE` / `RETURNING`), so an inner edit that grows the source SELECT
 * can never bleed into a structured tail.
 *
 * Returns `null` when the statement no longer classifies as an INSERT…SELECT
 * (`bodyKind !== 'select'`) — the drill has gone stale (e.g. an outer edit
 * rewrote the statement into a VALUES-form INSERT, or the target is no longer
 * parseable as INSERT at all) and the caller should clear it.
 */
import type { ParseOptions, SqlRange } from '../../../../sql-grammar/model';
import { extractInsertOutline } from './extractInsertOutline';

export function resolveInsertSourceBodyEnd(
  source: string,
  statementRange: SqlRange,
  options: ParseOptions,
): number | null {
  const outline = extractInsertOutline(source, statementRange, options);
  if (outline.bodyKind !== 'select') return null;
  return outline.selectSourceEnd;
}
